import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import { ReactRenderer } from "@tiptap/react"

import { INTERNAL_ARTICLE_LINK_ICON_CLASS } from "@productlane/lib/src/help-center/internal-links"
import { NucleoFillIcon } from "@productlane/ui"

import type { Mark, Node as ProseMirrorNode } from "@tiptap/pm/model"
import type { EditorState } from "@tiptap/pm/state"
import type { Editor } from "@tiptap/react"

export interface ArticleLinkRange {
	from: number
	to: number
	href: string
	mark: Mark
}

// Adjacent text nodes carrying the same link mark are one link to the reader,
// so they get one pill rather than one per text node.
export const collectArticleLinkRanges = (
	doc: ProseMirrorNode,
): ArticleLinkRange[] => {
	const ranges: ArticleLinkRange[] = []

	doc.descendants((node, pos) => {
		if (!node.isText) return true

		const mark = node.marks.find((candidate) => candidate.type.name === "link")
		const href = typeof mark?.attrs.href === "string" ? mark.attrs.href : null
		if (!mark || !href) return true

		const previous = ranges.at(-1)
		if (previous?.to === pos && previous.href === href) {
			previous.to = pos + node.nodeSize
			return true
		}

		ranges.push({ from: pos, to: pos + node.nodeSize, href, mark })
		return true
	})

	return ranges
}

const iconWidget = (range: ArticleLinkRange, icon: string, editor: Editor) => {
	let renderer: ReactRenderer | null = null

	return Decoration.widget(
		range.from,
		() => {
			renderer = new ReactRenderer(
				() => (
					<NucleoFillIcon
						icon={icon}
						className="docs-inline-article-link-glyph"
					/>
				),
				{
					editor,
					as: "span",
					className: INTERNAL_ARTICLE_LINK_ICON_CLASS,
				},
			)
			return renderer.element as HTMLElement
		},
		{
			side: -1,
			marks: [range.mark],
			destroy: () => renderer?.destroy(),
		},
	)
}

export interface ArticleLinkPillsOptions {
	// null when the href points outside this help center.
	resolveArticle: (href: string) => { icon: string | null } | null
}

const articleLinkPillsKey = new PluginKey("articleLinkPills")

export const ArticleLinkPills = Extension.create<ArticleLinkPillsOptions>({
	name: "articleLinkPills",

	addOptions() {
		return { resolveArticle: () => null }
	},

	addProseMirrorPlugins() {
		const { resolveArticle } = this.options
		const editor = this.editor

		const build = (state: EditorState) => {
			const decorations: Decoration[] = []

			for (const range of collectArticleLinkRanges(state.doc)) {
				const article = resolveArticle(range.href)
				if (!article?.icon) continue

				// The widget carries the link mark so it renders inside the <a>,
				// which the pill styles hang off of.
				decorations.push(iconWidget(range, article.icon, editor))
			}

			return DecorationSet.create(state.doc, decorations)
		}

		return [
			new Plugin({
				key: articleLinkPillsKey,
				state: {
					init: (_, state) => build(state),
					apply: (tr, value, _oldState, newState) =>
						tr.docChanged ? build(newState) : value,
				},
				props: {
					decorations: (state) =>
						articleLinkPillsKey.getState(state) as DecorationSet,
				},
			}),
		]
	},
})
